import {EffectCallback, useCallback, useEffect, useState} from 'react';
import {freeze, produce} from 'immer';

import {ImmerHook} from './types';

export const useMountEffect = (func: EffectCallback) => useEffect(func, []);

export function track(eventName: string, props: {[key: string]: string | boolean | number | string[]} = {}) {
  mixpanel.track(eventName, {...props, version: `Segmented Onboarding`});
}

/** Hooks related utils */

// For ts type inference
export function useImmer<S = any>(initialValue: S | (() => S)): ImmerHook<S>;

export function useImmer(initialValue: any) {
  const [val, updateValue] = useState(() =>
    freeze(typeof initialValue === `function` ? initialValue() : initialValue, true),
  );
  return [
    val,
    useCallback((updater) => {
      if (typeof updater === `function`) updateValue(produce(updater));
      else updateValue(freeze(updater));
    }, []),
  ];
}

// Media query hooks
export function useSmallScreen() {
  return useMediaQuery({maxWidth: 600});
}

type MediaQueryMatch = {
  minWidth?: number;
  maxWidth?: number;
};

// https://usehooks-ts.com/react-hook/use-media-query
export function useMediaQuery(matchParam: MediaQueryMatch): boolean {
  const query = `${matchParam.minWidth !== undefined ? `(min-width: ${matchParam.minWidth}px)` : ``} ${
    matchParam.minWidth !== undefined && matchParam.maxWidth !== undefined ? `and` : ``
  } ${matchParam.maxWidth !== undefined ? `(max-width: ${matchParam.maxWidth}px` : ``}`;

  const getMatches = (query: string): boolean => {
    // Prevents SSR issues
    if (typeof window !== `undefined`) {
      return window.matchMedia(query).matches;
    }
    return false;
  };

  const [matches, setMatches] = useState<boolean>(getMatches(query));

  function handleChange() {
    setMatches(getMatches(query));
  }

  useEffect(() => {
    const matchMedia = window.matchMedia(query);

    // Triggered at the first client-side load and if query changes
    handleChange();

    // Listen matchMedia; check deprecated method for old browser
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener(`change`, handleChange);
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener(`change`, handleChange);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  return matches;
}
